
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";
import { format } from "date-fns";

const RECORDS_PER_PAGE = 15; // Keep existing limit

interface TimetableDataItem {
  id: string;
  name: string;
  classes: Array<{
    id: string;
    subject: string;
    startTime: string;
    endTime: string;
    room: string;
    status?: 'present' | 'late' | 'absent';
  }>;
}

/**
 * Export timetable data as JPG with pagination support - ULTRA FAST
 */
export const printTimetableDataPaginatedAsJPG = async (
  data: TimetableDataItem[],
  reportTitle: string,
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting ultra-fast ${reportTitle} JPG download...`, { data: data.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating ${reportTitle} JPG...`);
    
    const { content, filename } = generateTimetableContent(data, reportTitle, startPage, endPage);
    
    console.log('Starting ultra-fast JPG download with filename:', filename);
    
    // Start download immediately
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download ${reportTitle}`);
      }
    });
    
    // Quick success notification
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`${reportTitle} ${pageText} downloaded as JPG!`, {
      duration: 2000
    });
    
  } catch (error) {
    console.error(`Error downloading paginated ${reportTitle}:`, error);
    toast.error(`Failed to download ${reportTitle}`);
  }
};

/**
 * Export timetable data as PDF with pagination support - ULTRA FAST
 */
export const printTimetableDataPaginatedAsPDF = async (
  data: TimetableDataItem[],
  reportTitle: string,
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting ultra-fast ${reportTitle} PDF download...`, { data: data.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating ${reportTitle} PDF...`);
    
    const { content, filename } = generateTimetableContent(data, reportTitle, startPage, endPage);
    
    console.log('Starting ultra-fast PDF download with filename:', filename);
    
    // Start download immediately
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download ${reportTitle}`);
      }
    });
    
    // Quick success notification
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`${reportTitle} ${pageText} downloaded as PDF!`, {
      duration: 2000
    });
    
  } catch (error) {
    console.error(`Error downloading paginated ${reportTitle}:`, error);
    toast.error(`Failed to download ${reportTitle}`);
  }
};

const generateTimetableContent = (data: TimetableDataItem[], reportTitle: string, startPage: number, endPage: number) => {
  // Flatten all classes from all teachers with teacher info
  const allClasses = data.flatMap(teacher => 
    teacher.classes.map(classItem => ({
      ...classItem,
      teacherName: teacher.name,
      teacherId: teacher.id
    }))
  );

  const totalPages = Math.ceil(allClasses.length / RECORDS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  console.log('Calculated pages:', { totalPages, actualStartPage, actualEndPage });
  
  // Calculate which records to include
  const startIndex = (actualStartPage - 1) * RECORDS_PER_PAGE;
  const endIndex = actualEndPage * RECORDS_PER_PAGE;
  const recordsToShow = allClasses.slice(startIndex, endIndex);
  
  console.log('Records to show:', { startIndex, endIndex, count: recordsToShow.length });
  
  // Group records by pages
  const pages: typeof recordsToShow[] = [];
  for (let i = 0; i < recordsToShow.length; i += RECORDS_PER_PAGE) {
    pages.push(recordsToShow.slice(i, i + RECORDS_PER_PAGE));
  }
  
  // Generate content for all pages using standardized structure
  const allPagesContent = pages.map((pageRecords, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`${reportTitle} - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Records ${(currentPageNumber - 1) * RECORDS_PER_PAGE + 1} - ${Math.min(currentPageNumber * RECORDS_PER_PAGE, allClasses.length)} of ${allClasses.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Teacher</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Day</th>
                <th>Month</th>
                <th>Year</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Room</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pageRecords.map((record, index) => {
                const displayId = (startIndex + (pageIndex * RECORDS_PER_PAGE) + index + 1).toString();
                
                let startTime = 'Invalid Time';
                let endTime = 'Invalid Time';
                let dateStr = 'Invalid Date';
                let dayStr = 'Invalid Day';
                let monthStr = 'Invalid Month';
                let yearStr = 'Invalid Year';
                
                try {
                  const startDate = new Date(record.startTime);
                  const endDate = new Date(record.endTime);
                  
                  if (!isNaN(startDate.getTime())) {
                    startTime = format(startDate, 'h:mm a');
                    dateStr = format(startDate, 'dd');
                    dayStr = format(startDate, 'EEEE');
                    monthStr = format(startDate, 'MMMM');
                    yearStr = format(startDate, 'yyyy');
                  }
                  
                  if (!isNaN(endDate.getTime())) {
                    endTime = format(endDate, 'h:mm a');
                  }
                } catch (error) {
                  console.error('Error formatting time:', error);
                }
                
                const statusClass = record.status === 'absent' ? 'status-absent' : 
                                  record.status === 'late' ? 'status-late' : 'status-present';
                
                return `
                  <tr>
                    <td>${displayId}</td>
                    <td>${record.teacherName || 'Unknown'}</td>
                    <td>${record.subject || 'N/A'}</td>
                    <td>${dateStr}</td>
                    <td>${dayStr}</td>
                    <td>${monthStr}</td>
                    <td>${yearStr}</td>
                    <td>${startTime}</td>
                    <td>${endTime}</td>
                    <td>${record.room || 'N/A'}</td>
                    <td class="${statusClass}">${record.status || 'Not Set'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          ${getSignatureHtml('report')}
        </div>
        
        ${getFooterHtml('report')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      font-size: 0.7em;
    }
    th, td {
      padding: 6px 4px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      vertical-align: top;
      font-size: 0.8em;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
      font-size: 0.75em;
    }
    .status-present {
      color: #22c55e;
      font-weight: 500;
    }
    .status-absent {
      color: #ef4444;
      font-weight: 500;
    }
    .status-late {
      color: #f59e0b;
      font-weight: 500;
    }
  `;

  const printDocument = createPrintDocument(allPagesContent, `${reportTitle}_Paginated`, styles);
  
  const safeTitle = reportTitle.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = actualStartPage === actualEndPage 
    ? `${safeTitle}_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
    : `${safeTitle}_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};
